/* eslint-disable react/display-name */
/* eslint-disable react/require-default-props */
/* eslint-disable react/no-unused-prop-types */

import React, { memo, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Column, CellProps } from 'react-table';
import { ShiftRowViewModel, ShiftLayoutVariant } from '../utils/RequisitionDetailsViewModel';
import { GenericDataTable } from './components/TableComponents';
import { DayCell } from './cells/DayCell';
import { TimeCell } from './cells/TimeCell';
import { MinHoursCell } from './cells/MinHoursCell';
import colors from '../../../../../styles/colors';

export interface ShiftGroupTableProps {
  rows: ShiftRowViewModel[];
  variant: ShiftLayoutVariant;
}

export const ShiftGroupTable = memo(({ rows, variant }: ShiftGroupTableProps) => {
  const { t } = useTranslation('requisition');

  const columns = useMemo<Column<ShiftRowViewModel>[]>(() => {
    const baseCols: Column<ShiftRowViewModel>[] = [
      {
        // eslint-disable-next-line @typescript-eslint/no-unnecessary-type-assertion
        Header: t('shiftHeader.day') as string,
        accessor: 'dayLabel',
        width: '70px',
        disableSortBy: true,
        Cell: ({ row }: CellProps<ShiftRowViewModel>) => {
          const hasData =
            row.original.slot1Start?.exists ||
            row.original.slot1End?.exists ||
            row.original.slot2Start?.exists ||
            row.original.slot2End?.exists;
          return (
            <DayCell
              dayLabel={row.original.dayLabel}
              isDayRequired={!!row.original.isDayRequired}
              isMismatch={row.original.isDayMismatch}
              hasData={hasData}
            />
          );
        },
      },
      {
        // eslint-disable-next-line @typescript-eslint/no-unnecessary-type-assertion
        Header: t('shiftHeader.startTime') as string,
        accessor: 'slot1Start',
        width: '80px',
        disableSortBy: true,
        Cell: ({ row }: CellProps<ShiftRowViewModel>) => <TimeCell {...row.original.slot1Start} />,
      },
      {
        // eslint-disable-next-line @typescript-eslint/no-unnecessary-type-assertion
        Header: t('shiftHeader.endTime') as string,
        accessor: 'slot1End',
        width: '80px',
        disableSortBy: true,
        Cell: ({ row }: CellProps<ShiftRowViewModel>) => <TimeCell {...row.original.slot1End} />,
      },
    ];

    if (variant === 'double') {
      baseCols.push({
        // eslint-disable-next-line @typescript-eslint/no-unnecessary-type-assertion
        Header: t('shiftHeader.startTime') as string,
        id: 'slot2Start',
        accessor: 'slot2Start',
        width: '80px',
        disableSortBy: true,
        Cell: ({ row }: CellProps<ShiftRowViewModel>) => (
          <TimeCell {...(row.original.slot2Start ?? { timeLabel: undefined, isRequired: false, exists: false })} />
        ),
      });
      baseCols.push({
        // eslint-disable-next-line @typescript-eslint/no-unnecessary-type-assertion
        Header: t('shiftHeader.endTime') as string,
        id: 'slot2End',
        accessor: 'slot2End',
        width: '80px',
        disableSortBy: true,
        Cell: ({ row }: CellProps<ShiftRowViewModel>) => (
          <TimeCell {...(row.original.slot2End ?? { timeLabel: undefined, isRequired: false, exists: false })} />
        ),
      });
    }

    baseCols.push({
      // eslint-disable-next-line @typescript-eslint/no-unnecessary-type-assertion
      Header: t('shiftHeader.minimumHours') as string,
      accessor: 'minHoursLabel',
      width: '80px',
      disableSortBy: true,
      Cell: ({ row }: CellProps<ShiftRowViewModel>) => (
        <MinHoursCell minHoursLabel={row.original.minHoursLabel} isMismatch={row.original.minHoursMismatch} />
      ),
    });

    return baseCols;
  }, [t, variant]);

  const getRowBgColor = React.useCallback((row: ShiftRowViewModel) => {
    return row.isRowMismatch ? colors.requisitionDetails.mismatchCellBg : undefined;
  }, []);

  return <GenericDataTable columns={columns} data={rows} getRowBgColor={getRowBgColor} />;
});

ShiftGroupTable.displayName = 'ShiftGroupTable';
